import React from "react";
import _ from "lodash";
import NumberFormat from "../../common/NumberFormat";
import VaultAccountViewByNetworkModal from "../../vault/VaultAccountViewByNetworkModal";
import NetworkEnsureResourcesButton from "./NetworkEnsureResources";
import NetworkStatusIndicator from "components/common/NetworkStatusIndicator";
import GoToPageSelector from "components/common/GoToPageSelector";
import UpdateNetworkBillableTypes from "./network-management/UpdateNetworkBillableTypes";
import SyncVerifyNetworkButton from "./network-management/SyncVerifyNetworkButton";
import UpdateAdServingCostModal from "./network-management/UpdateAdServingCostModal";
import UpdateNetworkStatusModal from "./network-management/UpdateNetworkStatusModal";

const thClass =
  "px-4 py-2 text-gray-600 text-left text-xs tracking-wider uppercase";
const tdClass = "px-4 py-2";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 shadow hover:bg-gray-300 text-gray-900 font-semibold";

class NetworksTable extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isAccountModalOpen: false,
      isUpdateAdServingCostModalOpen: false,
      isUpdateStatusModalOpen: false,
      currentNetwork: null,
    };

    this.handleOpenAccountModal = this.handleOpenAccountModal.bind(this);
    this.handleOpenUpdateAdServingCostModal =
      this.handleOpenUpdateAdServingCostModal.bind(this);
    this.handleOpenUpdateStatusModal =
      this.handleOpenUpdateStatusModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.getNetworkActionPaths = this.getNetworkActionPaths.bind(this);
  }

  handleCloseModal() {
    this.setState({
      isAccountModalOpen: false,
      isUpdateAdServingCostModalOpen: false,
      isUpdateStatusModalOpen: false,
      currentNetwork: null,
    });
  }

  handleOpenAccountModal(network) {
    this.setState({ isAccountModalOpen: true, currentNetwork: network });
  }

  handleOpenUpdateAdServingCostModal(network) {
    this.setState({
      isUpdateAdServingCostModalOpen: true,
      currentNetwork: network,
    });
  }

  handleOpenUpdateStatusModal(network) {
    this.setState({
      isUpdateStatusModalOpen: true,
      currentNetwork: network,
    });
  }

  getNetworkActionPaths(networkId) {
    return [
      {
        name: "UPR Segmentation",
        path: `/ops-mgmt/account/network/${networkId}/upr-segmentation`,
      },
      {
        name: "Custom UPR",
        path: `/ops-mgmt/account/network/${networkId}/upr-setting`,
      },
      {
        name: "CST Config",
        path: `/ops-mgmt/account/network/${networkId}/cst-config`,
      },
      {
        name: "Network Features",
        path: `/ops-mgmt/account/network/${networkId}/features`,
      },
      {
        name: "Code Snippet",
        path: `/ops-mgmt/account/network/${networkId}/code-snippet`,
      },
      {
        name: "Virtual Placements",
        path: `/ops-mgmt/account/network/${networkId}/virtual-placements`,
      },
      {
        name: "App Configuration",
        path: `/ops-mgmt/account/network/${networkId}/ac-raw-config`,
      },
    ];
  }

  render() {
    const { items, hideAction } = this.props;
    const {
      isAccountModalOpen,
      isUpdateAdServingCostModalOpen,
      isUpdateStatusModalOpen,
      currentNetwork,
    } = this.state;

    return (
      <>
        <table className="shadow divide-y min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className={thClass} style={{ width: "25rem" }}>
                Network
              </th>
              <th className={thClass + " text-right"}>Cost CPM</th>
              <th className={thClass + " text-right"}>Free Quota</th>
              <th className={thClass + " text-left"}>Props</th>

              {!hideAction && (
                <th
                  className={thClass + " text-right"}
                  style={{ width: "27.5rem" }}
                >
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y bg-white text-sm">
            {items.map((item) => {
              return (
                <tr key={item.networkId} className="hover:bg-gray-100">
                  <td className={tdClass}>
                    <span className="text-lg font-semibold text-gray-800">
                      {item.networkId} - {item.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <NetworkStatusIndicator
                        status={item.status}
                        showTooltip={false}
                        key={`${item.networkId}-${item.status}`}
                      ></NetworkStatusIndicator>
                      <div className="text-xs font-medium tracking-wide text-gray-600">
                        {item.status}
                      </div>
                    </div>
                  </td>

                  <td className={tdClass + " text-right"}>
                    <div className="border-b pb-1">
                      <span className="text-xs text-gray-600">algo </span>
                      <NumberFormat
                        value={item.algoCostCpm}
                        prefix="$"
                        format="0,0.0000"
                      ></NumberFormat>
                      <div className="text-xs leading-none text-gray-600">
                        USD
                      </div>
                    </div>

                    <div>
                      {item.adServingCost ? (
                        <>
                          <NumberFormat
                            value={item.adServingCost.costCpm}
                            prefix="$"
                            format="0,0.0000"
                          ></NumberFormat>
                          <div
                            className={`text-xs leading-none ${
                              !item.adServingCost.costCpmCurrency ||
                              item.adServingCost.costCpmCurrency === "USD"
                                ? "text-gray-600"
                                : "text-purple-600"
                            }`}
                          >
                            {item.adServingCost.costCpmCurrency || "USD"}
                          </div>
                        </>
                      ) : (
                        "-"
                      )}
                    </div>
                  </td>
                  <td className={tdClass + " text-right"}>
                    {item.adServingCost ? (
                      <NumberFormat
                        value={item.adServingCost.freeQuota}
                        format="0,0a"
                      ></NumberFormat>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className={tdClass + " text-left"}>
                    <div className="border-b my-1 pb-1 text-xs text-gray-900">
                      <span>
                        Billable Demand Types ({item.billableDemandTypes.length}
                        ):{" "}
                      </span>
                      <DemandTypesSection
                        demandTypes={item.billableDemandTypes}
                      ></DemandTypesSection>
                    </div>

                    <div className="border-b mb-1 mt-2 pb-1 text-xs text-gray-900">
                      <span>
                        Billable Request Types (
                        {item.billableRequestTypes.length}):{" "}
                      </span>
                      <RequestTypesSection
                        requestTypes={item.billableRequestTypes}
                      ></RequestTypesSection>
                    </div>

                    <div className="mb-1 mt-2 text-xs text-gray-900">
                      <span>
                        Billable Inventory Formats (
                        {item.billableInventoryFormats.length}):{" "}
                      </span>
                      <InventoryFormatsSection
                        inventoryFormats={item.billableInventoryFormats}
                      ></InventoryFormatsSection>
                    </div>
                  </td>

                  {!hideAction && (
                    <td className={tdClass + " text-right"}>
                      {
                        // item.status !== "BUSINESS_TERMINATED" &&
                        item.accountType !== "ARCHIVED" &&
                        item.accountType !== "PROCESSING" &&
                        item.accountType !== "FAILED" ? (
                          <>
                            <GoToPageSelector
                              goToPaths={this.getNetworkActionPaths(
                                item.networkId
                              )}
                            ></GoToPageSelector>
                            <br></br>
                          </>
                        ) : (
                          "-"
                        )
                      }

                      <div className="flex items-center justify-end gap-2">
                        <UpdateNetworkBillableTypes
                          networkId={item.networkId}
                          name={item.name}
                          billableDemandTypes={item.billableDemandTypes}
                          billableRequestTypes={item.billableRequestTypes}
                          billableInventoryFormats={
                            item.billableInventoryFormats
                          }
                        ></UpdateNetworkBillableTypes>

                        <NetworkEnsureResourcesButton
                          networkId={item.networkId}
                        ></NetworkEnsureResourcesButton>

                        <button
                          type="button"
                          className={buttonActionClass}
                          onClick={() => this.handleOpenAccountModal(item)}
                        >
                          GAM Account
                        </button>
                      </div>

                      <div className="mt-2 flex items-center justify-end gap-2">
                        <SyncVerifyNetworkButton
                          networkId={item.networkId}
                          name={item.name}
                          status={item.status}
                          customClassName={
                            "flex items-center justify-end gap-2"
                          }
                          updateNetworkStatus={this.props.updateNetworkStatus}
                        ></SyncVerifyNetworkButton>
                        <button
                          type="button"
                          className={buttonActionClass}
                          onClick={() => this.handleOpenUpdateStatusModal(item)}
                        >
                          Update Status
                        </button>
                        <button
                          type="button"
                          className={buttonActionClass}
                          onClick={() =>
                            this.handleOpenUpdateAdServingCostModal(item)
                          }
                        >
                          Update Ad Serving Cost
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {isAccountModalOpen && (
          <VaultAccountViewByNetworkModal
            network={currentNetwork}
            isOpenModal={isAccountModalOpen}
            handleClose={this.handleCloseModal}
          ></VaultAccountViewByNetworkModal>
        )}

        {isUpdateAdServingCostModalOpen && (
          <UpdateAdServingCostModal
            network={currentNetwork}
            updateData={this.props.updateNetworkCost}
            handleClose={this.handleCloseModal}
          ></UpdateAdServingCostModal>
        )}

        {isUpdateStatusModalOpen && (
          <UpdateNetworkStatusModal
            network={currentNetwork}
            updateData={this.props.updateNetworkStatus}
            handleClose={this.handleCloseModal}
          ></UpdateNetworkStatusModal>
        )}
      </>
    );
  }
}

class InventoryFormatsSection extends React.PureComponent {
  render() {
    const { inventoryFormats } = this.props;
    if (!inventoryFormats) {
      return "-";
    }

    const dts = inventoryFormats;

    return (
      // <div className="flex gap-2 whitespace-no-wrap overflow-x-auto w-full">
      <div className="-mb-1">
        {dts.map((d) => {
          return (
            <div
              key={d}
              className="rounded mb-1 mr-2 inline-block bg-gray-300 px-2 text-xs text-gray-700"
            >
              {d}
            </div>
          );
        })}
      </div>
    );
  }
}

class DemandTypesSection extends React.PureComponent {
  render() {
    const { demandTypes } = this.props;
    if (!demandTypes) {
      return "-";
    }

    const dts = demandTypes;

    return (
      // <div className="flex gap-2 whitespace-no-wrap overflow-x-auto w-full">
      <div className="-mb-1">
        {dts.map((d) => {
          return (
            <div
              key={d}
              className="rounded mb-1 mr-2 inline-block bg-gray-300 px-2 text-xs text-gray-700"
            >
              {d}
            </div>
          );
        })}
      </div>
    );
  }
}

class RequestTypesSection extends React.PureComponent {
  render() {
    const { requestTypes } = this.props;
    if (!requestTypes) {
      return "-";
    }

    const dts = requestTypes;

    return (
      // <div className="flex gap-2 whitespace-no-wrap overflow-x-auto w-full">
      <div className="-mb-1">
        {dts.map((d) => {
          return (
            <div
              key={d}
              className="rounded mb-1 mr-2 inline-block bg-gray-300 px-2 text-xs text-gray-700"
            >
              {d}
            </div>
          );
        })}
      </div>
    );
  }
}

export default NetworksTable;
